import { Check, Close, EditOutlined, X } from 'components/icons';
import { FC, useCallback, useState } from 'react';

import { Tooltip } from 'components/ui/tooltip';
import { errorAlert, successAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { GHOST_ICON_BUTTON } from './ghostIconButton';

interface EditableVisibleForCellProps {
    id: number;
    visibleFor: string | null;
    onSave: (id: number, visibleFor: string | null) => Promise<unknown>;
}

// Grey pill for a single domain. In the editor it carries a remove button; in the read view it is
// a plain label: grey fill, ~24px, rounded.
const DomainChip = ({ domain, onRemove }: { domain: string; onRemove?: () => void }) => (
    <span
        className={cn(
            'bg-grey-200 text-grey-700 inline-flex h-6 max-w-full items-center gap-1 rounded-2xl pl-2 text-[0.8125rem]',
            onRemove ? 'pr-1' : 'pr-2'
        )}
    >
        {/* Truncate a long domain (with a title) so it can't overflow the narrow display column. */}
        <span className="truncate" title={domain}>
            {domain}
        </span>
        {onRemove ? (
            <button
                type="button"
                aria-label={t('remove')}
                onClick={onRemove}
                className="text-grey-500 hover:text-grey-700 inline-flex cursor-pointer appearance-none border-0 bg-transparent p-0"
            >
                <X className="size-3.5" />
            </button>
        ) : null}
    </span>
);

// The stored value is the raw jsonb string — an array of work-email suffixes, e.g. ["@crespect.com"]. The UI never
// shows that JSON: it parses the array into individual domain chips. A null/blank or non-array value yields no chips.
const parseVisibleForDomains = (visibleFor: string | null): string[] => {
    if (visibleFor === null || visibleFor.trim() === '') {
        return [];
    }
    try {
        const parsed: unknown = JSON.parse(visibleFor);
        return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
    } catch {
        return [];
    }
};

// Inline domain-chips editor for system_privilege.visible_for. The user types domains separated by commas (or Enter);
// each becomes a chip. On save the chips are serialised back to a JSON array string (or null to clear → visible to
// everyone). Draft state lives in the cell so typing doesn't rebuild the parent's columns memo.
const EditableVisibleForCell: FC<EditableVisibleForCellProps> = ({ id, visibleFor, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [domains, setDomains] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [saving, setSaving] = useState(false);

    const savedDomains = parseVisibleForDomains(visibleFor);

    const confirm = useCallback(async () => {
        // Fold any value the user typed but hasn't committed to a chip yet, then de-duplicate.
        const pending = inputValue.trim();
        const unique = Array.from(
            new Set(
                [...domains, ...(pending === '' ? [] : [pending])]
                    .map((domain) => domain.trim())
                    .filter((domain) => domain !== '')
            )
        );
        setSaving(true);
        try {
            await onSave(id, unique.length === 0 ? null : JSON.stringify(unique));
            successAlert(t('roles-and-permissions.visible-for-updated'));
            setEditing(false);
        } catch {
            errorAlert(t('roles-and-permissions.visible-for-update-error'));
        } finally {
            setSaving(false);
        }
    }, [domains, inputValue, id, onSave]);

    // A comma or Enter commits everything typed so far into chips; Backspace on an empty input pops
    // the last chip. Kept as named helpers (not inline generic handlers) for clarity.
    const commitDomain = useCallback((value: string) => {
        const trimmed = value.trim();
        if (trimmed === '') return;
        setDomains((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
    }, []);

    const changeDomainInput = useCallback(
        (value: string) => {
            if (value.includes(',')) {
                value.split(',').forEach(commitDomain);
                setInputValue('');
            } else {
                setInputValue(value);
            }
        },
        [commitDomain]
    );

    const pressKeyInDomainInput = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                commitDomain(inputValue);
                setInputValue('');
            } else if (event.key === 'Backspace' && inputValue === '') {
                setDomains((prev) => prev.slice(0, -1));
            } else if (event.key === 'Escape') {
                event.preventDefault();
                setEditing(false);
            }
        },
        [commitDomain, inputValue]
    );

    if (!editing) {
        return (
            // Fill the column so the pen pins to the right edge — all pens line up regardless of how
            // many chips a row has. The chips box takes the remaining width (min-w-0 so a long domain
            // truncates instead of overflowing into the next column); chips wrap inside it.
            <div className="flex w-full items-center gap-1">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                    {savedDomains.length > 0 ? (
                        savedDomains.map((domain) => <DomainChip key={domain} domain={domain} />)
                    ) : (
                        <span className="text-grey-500">{t('roles-and-permissions.visible-for-everyone')}</span>
                    )}
                </div>
                <Tooltip content={t('roles-and-permissions.edit-visible-for')}>
                    <button
                        type="button"
                        aria-label={t('roles-and-permissions.edit-visible-for')}
                        className={cn(GHOST_ICON_BUTTON, 'text-grey-600 ml-auto')}
                        onClick={() => {
                            setDomains(savedDomains);
                            setInputValue('');
                            setEditing(true);
                        }}
                    >
                        <EditOutlined fontSize="small" />
                    </button>
                </Tooltip>
            </div>
        );
    }

    return (
        // Fills the cell, which the CSS grow-on-edit rule (RolesAndPermissionsTable.module.css)
        // widens to fit the chips editor + full placeholder — matching develop's grown edit column.
        <div className="flex w-full items-start gap-1">
            <div className="border-input focus-within:border-ring focus-within:ring-ring/50 flex min-h-9 flex-1 flex-wrap items-center gap-1 rounded-md border bg-transparent px-2 py-1 focus-within:ring-3">
                {domains.map((domain) => (
                    <DomainChip
                        key={domain}
                        domain={domain}
                        onRemove={() => setDomains((prev) => prev.filter((d) => d !== domain))}
                    />
                ))}
                <input
                    autoFocus
                    // Full-width line so it wraps below any chips (like develop) and always shows the
                    // whole placeholder, instead of sharing a line with a chip and getting cropped.
                    className="new:placeholder:text-muted-foreground w-full min-w-0 appearance-none border-0 bg-transparent p-0 text-base outline-none md:text-sm"
                    placeholder={t('roles-and-permissions.visible-for-placeholder')}
                    value={inputValue}
                    onChange={(event) => changeDomainInput(event.target.value)}
                    onKeyDown={pressKeyInDomainInput}
                />
            </div>
            <button
                type="button"
                aria-label={t('save')}
                className={cn(GHOST_ICON_BUTTON, 'text-secondary')}
                disabled={saving}
                onClick={() => void confirm()}
            >
                <Check fontSize="small" />
            </button>
            <button
                type="button"
                aria-label={t('cancel')}
                className={cn(GHOST_ICON_BUTTON, 'text-grey-600')}
                disabled={saving}
                onClick={() => setEditing(false)}
            >
                <Close fontSize="small" />
            </button>
        </div>
    );
};

export default EditableVisibleForCell;
